import React from 'react';

import { faSpinnerThird, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_IDENTIFIERS_RESOURCE } from 'src/features/membership/api/identifier/useListIdentifiers';
import { MembershipIdentifier } from 'src/features/membership/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import { useDeleteIdentifier } from 'src/features/membership/api/identifier/useDeleteIdentifier';
import { useParking } from 'src/providers/ParkingProvider';

interface IdentifierFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedIdentifier: MembershipIdentifier;
    children?: React.ReactNode;
}

function DeleteIdentifierFormModal<TData>({
    selectedIdentifier,
    semantics,
    children,
}: IdentifierFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { membershipId = ''} = useParams();
    const { parkingId } = useParking();

    const { mutate: deleteIdentifier, isPending: isPendingDelete } = useDeleteIdentifier({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_IDENTIFIERS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(identifiersMessages.deleteIdentifierSuccess),
                description: formatMessage(identifiersMessages.deleteIdentifierSuccessDescription, {
                    entry: selectedIdentifier?.identifier.value,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deleteIdentifier({ parkingId, identifierId: selectedIdentifier.id, membershipId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={faTrash} />
                    <div>
                        <DialogTitle>
                            <FormattedMessage
                                {...generalMessages.deleteEntry}
                                values={{ entry: semantics?.entryName?.toLowerCase() }}
                            />
                        </DialogTitle>
                        <DialogDescription>
                            <FormattedMessage
                                {...identifiersMessages.deleteIdentifierSubtitle}
                                values={{ entry: selectedIdentifier.identifier.value }}
                            />
                        </DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            {...identifiersMessages.confirmDeleteIdentifier}
                            values={{ entry: selectedIdentifier.identifier.value }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                {...generalMessages.cancel}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            {...generalMessages.deleteEntry}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteIdentifierFormModal;
